import React from 'react';
import { Box, styled } from '@mui/material';
import { match } from 'ts-pattern';
import { Control } from 'react-hook-form';
import VerticalTabs from 'components/new/VerticalTabs';
import { useVerticalTabs } from 'components/new/VerticalTabs/useVerticalTabs';
import PortalGeneral from './General';
import PortalOnboarding from './Onboarding';
import PortalBranding from './Branding';
import { FormValues } from '../types';
import { useTheme } from '@mui/material/styles';

enum PortalSettingsTab {
  General = 'portal-general',
  Onboarding = 'portal-onboarding',
  Branding = 'portal-branding',
}

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(theme.spacingSizes.lg, 0),
  maxWidth: '600px',
}));

type Props = {
  control: Control<FormValues>;
  hasLinkedPortal: boolean;
  hasGeneralSubTabErrors: boolean;
  hasBrandingSubTabErrors: boolean;
  sharedFieldWarningText: string;
  sharedFieldDotColor: string;
};

const PortalSettings: React.FC<Props> = ({
  control,
  hasLinkedPortal,
  hasGeneralSubTabErrors,
  hasBrandingSubTabErrors,
  sharedFieldWarningText,
  sharedFieldDotColor,
}) => {
  const theme = useTheme();
  const tabErrorDotColor = theme.palette.error.main;

  const { selectedTab, setSelectedTab } = useVerticalTabs<PortalSettingsTab>(
    PortalSettingsTab.General,
  );

  const tabs = [
    {
      label: 'General',
      id: PortalSettingsTab.General,
      dotColor: hasGeneralSubTabErrors ? tabErrorDotColor : undefined,
    },
    {
      label: 'Onboarding',
      id: PortalSettingsTab.Onboarding,
    },
    {
      label: 'Branding',
      id: PortalSettingsTab.Branding,
      dotColor: hasBrandingSubTabErrors ? tabErrorDotColor : undefined,
    },
  ];

  return (
    <VerticalTabs
      tabs={tabs}
      selectedTab={selectedTab as PortalSettingsTab}
      onTabChange={setSelectedTab}
      menuProps={{ sx: { width: 244 } }}
      renderContent={(tab) => (
        <ContentContainer>
          {match(tab)
            .with(PortalSettingsTab.General, () => (
              <PortalGeneral
                control={control}
                hasLinkedPortal={hasLinkedPortal}
                sharedFieldWarningText={sharedFieldWarningText}
                sharedFieldDotColor={sharedFieldDotColor}
              />
            ))
            .with(PortalSettingsTab.Onboarding, () => <PortalOnboarding />)
            .with(PortalSettingsTab.Branding, () => (
              <PortalBranding
                control={control}
                hasLinkedPortal={hasLinkedPortal}
                sharedFieldWarningText={sharedFieldWarningText}
                sharedFieldDotColor={sharedFieldDotColor}
              />
            ))
            .otherwise(() => (
              <></>
            ))}
        </ContentContainer>
      )}
    />
  );
};

export default PortalSettings;
